import React, { useState, useEffect } from 'react';
import { Star, MessageCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { fetchReviews, getAverageRating } from '../mock';

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    setLoading(true);
    try {
      // Facebook Reviews integration ready
      // Cette fonction sera connectée à l'API Facebook Reviews
      const data = await fetchReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={20}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const averageRating = getAverageRating();

  return (
    <section id="reviews" className="py-20" style={{ backgroundColor: '#FAF6F4' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-800 mb-4">
            Avis Clients
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Découvrez ce que mes clientes pensent de mes prestations
          </p>

          {/* Note moyenne globale - affichée seulement s'il y a des avis */}
          {reviews.length > 0 && (
            <div className="inline-flex items-center gap-4 bg-white px-8 py-4 rounded-2xl" style={{ borderWidth: '2px', borderColor: '#E8B4D1' }}>
              <div className="text-center">
                <div className="text-5xl font-bold" style={{ color: '#D1477A' }}>{averageRating}</div>
                <div className="text-sm text-gray-600 mt-1">sur 5</div>
              </div>
              <div className="pl-4" style={{ borderLeftWidth: '2px', borderLeftColor: '#E8B4D1' }}>
                {renderStars(Math.round(parseFloat(averageRating)))}
                <p className="text-sm text-gray-600 mt-2">{reviews.length} avis</p>
              </div>
            </div>
          )}
        </div>

        {/* Bouton Laisser un avis */}
        <div className="text-center mb-12">
          <Button
            onClick={() => window.open('https://www.facebook.com/profile.php?id=61584927464807', '_blank')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
          >
            <MessageCircle className="mr-2" size={20} />
            Laisser un avis sur Facebook
            <ExternalLink className="ml-2" size={16} />
          </Button>
        </div>

        {/* Grille des avis */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: '#D1477A', borderTopColor: 'transparent' }}></div>
            <p className="mt-4 text-gray-600">Chargement des avis...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 max-w-2xl mx-auto">
            <MessageCircle className="mx-auto mb-4 text-gray-400" size={64} />
            <h3 className="text-2xl font-semibold text-gray-700 mb-3">
              Aucun avis pour le moment
            </h3>
            <p className="text-gray-600 mb-6">
              Soyez la première à partager votre expérience avec Nailed by Chloé ! 
              Vos retours sont précieux et aident d'autres clientes à découvrir mes services.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {reviews.map((review) => (
              <Card 
                key={review.id}
                className="hover:shadow-xl transition-all duration-300 bg-white"
                style={{ borderWidth: '2px', borderColor: '#E8B4D1' }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl" style={{ color: '#D1477A' }}>
                        {review.firstName}
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatDate(review.date)}
                      </p>
                    </div>
                    {renderStars(review.rating)}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">
                    "{review.comment}"
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Note d'information */}
        <div className="mt-12 max-w-3xl mx-auto">
          <div className="bg-white p-6 rounded-lg" style={{ borderLeftWidth: '4px', borderLeftColor: '#D1477A' }}>
            <p className="text-gray-700 leading-relaxed text-sm">
              <span className="font-semibold" style={{ color: '#D1477A' }}>💡 Info :</span> Les avis affichés proviennent 
              de notre page Facebook. Vous pouvez laisser votre propre avis en cliquant sur le bouton ci-dessus.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
